import NProgress from 'nprogress';
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';

import { enabled } from '@/feature-toggle';

import { showModal } from './alerts';
import { DefaultLayout, type LayoutSettings } from './layouts/DefaultLayout';
import * as log from './logger';
import BaseRedirect from './pages/BaseRedirect.vue';
import NotFound404Page from './pages/NotFound404Page.vue';
import { buildLegacyRestoreRedirect } from './pages/restore-points-catalog/utils';
import { useAppInfoStore } from './stores/appInfo';
import { useMainStore } from './stores/main';
import { useSecondaryStore } from './stores/secondary';
import { useUserStore } from './stores/user';

const newDistributionsPageEnabled =
  // TODO: Remove this, when the new distributions page will be by default
  !enabled('legacyDistributions');

function onLoadError(err: unknown) {
  log.error('[router.js] Caught aync load error. Displaying refresh dialog', err);
  showModal({
    title: 'Software Updated',
    message:
      'A new version of Kasten system software has been installed and this page is now outdated. Please refresh the page.',
    submitButtonText: 'Refresh',
    submitButtonType: 'primary',
    cancelButton: true,
    backdrop: true,
    onSubmit: () => window.location.reload(),
  });
}

const shouldBlockMcSecondaryPages = async () => {
  const appInfoStore = useAppInfoStore();
  // TODO convert this store to useApiRequest to dedup this request
  if (appInfoStore.systemInfo === null) await appInfoStore.fetchSystemInfo();
  return appInfoStore.isMCPrimary || !appInfoStore.isMCCapable;
};

const hasClusterInfo = async () => {
  const secondaryStore = useSecondaryStore();
  await secondaryStore.fetchMcInfo();
  return !!secondaryStore.mcInfo?.clusterInfo;
};

/**
 * Lazy load pages to speed initial page load
 * See https://router.vuejs.org/en/advanced/lazy-loading.html
 */

// prettier-ignore
const AppsPage = () => import('./pages/apps').catch(onLoadError),
  AppsListPage = () => import('./pages/apps/NamespacesPage.vue').catch(onLoadError),
  MultipleNamespacesRestorePage = () => import('./pages/apps/MultipleRestorePage').catch(onLoadError),
  BlueprintsList = () => import('./pages/blueprints/BlueprintsList.vue').catch(onLoadError),
  BlueprintBindingsPage = () => import('./pages/blueprints/bindings/BlueprintBindingsPage.vue').catch(onLoadError),
  BlueprintBindings = () => import('./pages/blueprints/bindings/BlueprintBindings.vue').catch(onLoadError),
  BlueprintBindingEdit = () => import('./pages/blueprints/bindings/BlueprintBindingForm.vue').catch(onLoadError),
  BlueprintBindingCreate = () => import('./pages/blueprints/bindings/BlueprintBindingForm.vue').catch(onLoadError),
  BlueprintBindingView = () => import('./pages/blueprints/bindings/BlueprintBindingView.vue').catch(onLoadError),
  ClustersPage = () => import('./pages/multicluster/ClustersPage.vue').catch(onLoadError),
  DashboardPage = () => import('./pages/dashboard/DashboardPage.vue').catch(onLoadError),
  DataPage = () => import('./pages/data/DataPage.vue').catch(onLoadError),

  NewDistributionsPage = () => import('./pages/multicluster/newDistributions/DistributionsPage.vue').catch(onLoadError),
  NewDistributionView = () => import('./pages/multicluster/newDistributions/DistributionView.vue').catch(onLoadError),
  NewDistributionFormPage = () => import('./pages/multicluster/newDistributions/Form/DistributionFormPage.vue').catch(onLoadError),
  DistributionsPage = () => import('./pages/multicluster/Distributions.vue').catch(onLoadError),

  FeaturesPage = () => import('./pages/features/FeaturesPage.vue').catch(onLoadError),

  LocationProfiles = () => import('./pages/profiles/location/LocationProfiles.vue').catch(onLoadError),
  VeeamVaultCallback = () => import('./pages/profiles/location/VeeamVaultCallback.vue').catch(onLoadError),
  LocationProfileView = () => import('./pages/profiles/location/LocationProfileView.vue').catch(onLoadError),
  LocationProfileForm = () => import('./pages/profiles/location/LocationProfileFormPage.vue').catch(onLoadError),
  InfraProfiles = () => import('./pages/profiles/infra/InfraProfiles.vue').catch(onLoadError),
  InfraProfileForm = () => import('./pages/profiles/infra/Form/InfraProfileFormPage.vue').catch(onLoadError),

  JoinTokensPage = () => import('./pages/multicluster/joinTokens/JoinTokensPage.vue').catch(onLoadError),
  DrSetup = () => import('./pages/settings/DR/DrSetup/DrSetup.vue').catch(onLoadError),
  DrRestore = () => import('./pages/settings/DR/DrRestore/DrRestore.vue').catch(onLoadError),
  LicensingPage = () => import('./pages/multicluster/LicensingPage.vue').catch(onLoadError),
  LicenseSettings = () => import('./pages/settings/LicenseSettings.vue').catch(onLoadError),
  LoginPage = () => import('./pages/login/LoginPage.vue').catch(onLoadError),
  MessagePage = () => import('./pages/MessagePage.vue').catch(onLoadError),
  PasskeyManagementPage = () => import('./pages/settings/PasskeyManagement/PasskeyManagement.vue').catch(onLoadError),
  NewPoliciesMainPage = () => import('./pages/policies/new/PoliciesRoutePage.vue').catch(onLoadError),
  NewPoliciesPage = () => import('./pages/policies/new/PoliciesPage.vue').catch(onLoadError),
  PolicyViewPage = () => import('./pages/policies/new/PolicyViewPage.vue').catch(onLoadError),
  PolicyPresets = () => import('./pages/policies/PolicyPresets.vue').catch(onLoadError),
  PolicyPresetDetails = () => import('./pages/policy-presets/new/details/PresetDetails.vue').catch(onLoadError),
  PolicyRunPage = () => import('./pages/policy-run/PolicyRunPage.vue').catch(onLoadError),
  BatchRestoreDetailsPage = () => import('@/pages/batch-restore/details-page/BatchRestoreDetailsPage.vue').catch(onLoadError),
  ProfilesPage = () => import('./pages/profiles/ProfilesPage.vue').catch(onLoadError),
  RBAC = () => import('./pages/multicluster/RBAC.vue').catch(onLoadError),
  RegistrationsPage = () => import('./pages/settings/RegistrationsPage.vue').catch(onLoadError),
  ReportDetailsPage = () => import('./pages/reports/ReportDetailsPage.vue').catch(onLoadError),
  ReportsPage = () => import('./pages/reports/ReportsPage.vue').catch(onLoadError),
  MultiClusterPage = () => import('./pages/multicluster/MultiClusterPage.vue').catch(onLoadError),
  MCSecondaryPage = () => import('./pages/mc/MCSecondaryPage.vue').catch(onLoadError),
  MCJoin = () => import('./pages/mc/MCJoin.vue').catch(onLoadError),
  MCPromote = () => import('./pages/mc/MCPromote.vue').catch(onLoadError),
  RestorePointsCatalogPage = () => import('./pages/restore-points-catalog/RestorePointsCatalog.vue').catch(onLoadError),
  RestorePointValidateFormPage = () => import('./pages/restore-points-catalog/validateForm/RestorePointValidateFormPage.vue').catch(onLoadError),
  UserRolesPage = () => import('./pages/rbac/UserRolesPage.vue').catch(onLoadError),
  UserRoles = () => import('./pages/rbac/UserRoles.vue').catch(onLoadError),
  UserRoleAssignments = () => import('./pages/rbac/UserRoleAssignments.vue').catch(onLoadError),
  SettingsPage = () => import('./pages/settings/SettingsPage.vue').catch(onLoadError),
  StatusPage = () => import('./pages/status/StatusPage.vue').catch(onLoadError),
  SupportPage = () => import('./pages/SupportPage.vue').catch(onLoadError),
  SystemInfo = () => import('./pages/settings/SystemInfo.vue').catch(onLoadError),
  TransformSetSettings = () => import('./pages/transformSets').catch(onLoadError),
  CustomTransformSetsSettings = () => import('./pages/transformSets/CustomTransformSets.vue').catch(onLoadError),
  TransformSetsExamplesSettings = () => import('./pages/transformSets/TransformSetsExamples.vue').catch(onLoadError),
  TransformSetEditSettings = () => import('./pages/transformSets/EditTransformSet.vue').catch(onLoadError),
  TransformSetCreateSettings = () => import('./pages/transformSets/CreateTransformSet.vue').catch(onLoadError),
  UserProfilePage = () => import('./pages/UserProfilePage.vue').catch(onLoadError),
  VMsPage = () => import('./pages/vms/VMsPage.vue').catch(onLoadError),
  VMView = () => import('./pages/vms/VMView.vue').catch(onLoadError),
  VMsBatchRestorePage = () => import('./pages/vms/vmsBatchRestore/VMsBatchRestorePage.vue').catch(onLoadError);

export type RouteMetaLayout<LayoutComponent, Settings extends Record<string, unknown>> =
  | LayoutComponent
  | ({
      component: LayoutComponent;
    } & Settings);
declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    docs?: {
      path: string;
      description: string;
    };
    requiresAuth?: boolean;
    layout?: RouteMetaLayout<typeof DefaultLayout, LayoutSettings>;
    isMC?: boolean;
  }
}

export type Route = RouteRecordRaw & {
  meta?: {
    title?: string;
    docs?: {
      path: string;
      description: string;
    };
    requiresAuth?: boolean;
    layout?: RouteMetaLayout<typeof DefaultLayout, LayoutSettings>;
    isMC?: boolean;
  };
};

export const routes: Route[] = [
  {
    path: '/',
    component: BaseRedirect,
  },
  { path: '/clusters', redirect: { name: 'MCClusters' } },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardPage,
    meta: {
      title: 'Kasten • Cluster Dashboard',
      docs: {
        path: '/usage/overview',
        description: 'Cluster Dashboard',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/applications',
    name: 'AppsRoot',
    redirect: { name: 'Apps' },
    component: AppsPage,
    meta: {
      requiresAuth: true,
      layout: {
        component: DefaultLayout,
        wrapContentWithContainerClass: false,
      },
    },
    children: [
      {
        path: '',
        name: 'Apps',
        component: AppsListPage,
        meta: {
          title: 'Kasten • Applications',
          docs: {
            path: '/usage/protect',
            description: 'Protecting Applications',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'restore-multiple',
        name: 'AppsMultipleRestore',
        component: MultipleNamespacesRestorePage,
        meta: {
          title: 'Kasten • Applications • Multiple restore',
          docs: {
            path: '/usage/restore#restoring-multiple-applications',
            description: 'Restore multiple applications',
          },
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/vms',
    name: 'VMs',
    component: VMsPage,
    meta: {
      title: 'Kasten • VMs',
      docs: {
        path: '/usage/vm',
        description: 'VMs management',
      },
      requiresAuth: true,
      layout: {
        component: DefaultLayout,
        wrapContentWithContainerClass: false,
      },
    },
  },
  {
    path: '/vms/:namespace/:name',
    name: 'VMDetails',
    component: VMView,
    meta: {
      title: 'Kasten • VM Details',
      docs: {
        path: '/usage/vm',
        description: 'VM details',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/vms/batch-restore',
    name: 'VMsBatchRestore',
    component: VMsBatchRestorePage,
    meta: {
      title: 'Kasten • VMs • Batch Restore',
      docs: {
        path: '/usage/vm',
        description: 'VM batch restore',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/features',
    name: 'Features',
    component: FeaturesPage,
    meta: {
      title: 'Kasten • Features Toggle',
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: {
      title: 'Kasten • Sign In',
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: MessagePage,
    meta: {
      title: 'Kasten • Signed Out',
    },
    props: {
      titleProp: `You've been signed out.`,
      descProp: '',
      buttonTextProp: 'Sign Back In',
      buttonActionProp: 'reload',
    },
  },
  {
    path: '/message',
    name: 'Message',
    component: MessagePage,
    meta: {
      title: 'Kasten',
    },
  },
  {
    path: '/data',
    name: 'Data',
    component: DataPage,
    meta: {
      title: 'Kasten • Data Usage',
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/migrate/:namespace',
    name: 'Migrate',
    component: RestorePointsCatalogPage,
    meta: {
      title: 'Kasten • Export',
      docs: {
        path: '/usage/migration',
        description: 'Migrating Applications',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/policies',
    component: NewPoliciesMainPage,
    meta: {
      title: 'Kasten • Policies',
      docs: {
        path: '/usage/protect',
        description: 'Protecting Applications',
      },
      requiresAuth: true,
      layout: {
        component: DefaultLayout,
        wrapContentWithContainerClass: false,
      },
    },
    children: [
      {
        path: 'view/:namespace/:name',
        name: 'PolicyView',
        component: PolicyViewPage,
        meta: {
          title: 'Kasten • Policies • View',
          docs: {
            path: '/api/policies',
            description: 'Policies',
          },
          requiresAuth: true,
        },
      },
      {
        path: '/policies/:action?/:name?',
        name: 'Policies',
        component: NewPoliciesPage,
        meta: {
          title: 'Kasten • Policies',
          docs: {
            path: '/usage/protect',
            description: 'Protecting Applications',
          },
          requiresAuth: true,
        },
      },
      {
        path: '/policies/presets',
        name: 'PolicyPresets',
        component: PolicyPresets,
        meta: {
          title: 'Kasten • Policy Presets',
          docs: {
            path: '/usage/protect#using-policy-presets',
            description: 'Protecting Applications',
          },
          requiresAuth: true,
        },
      },
      {
        path: '/policies/presets/:namespace/:name',
        name: 'PolicyPresetDetails',
        component: PolicyPresetDetails,
        meta: {
          title: 'Kasten • Policy Preset Details',
          docs: {
            path: '/usage/protect#using-policy-presets',
            description: 'Protecting Applications',
          },
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/policy-run/:name',
    name: 'PolicyRun',
    component: PolicyRunPage,
    meta: {
      title: 'Kasten • Policy Run',
      docs: {
        path: '/usage/overview',
        description: 'Policy Run Activity',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/batch-restore/details/:name',
    name: 'BatchRestoreDetailsPage',
    component: BatchRestoreDetailsPage,
    meta: {
      title: 'Kasten • Batch Restore',
      docs: {
        path: '/usage/overview',
        description: 'Batch Restore Activity',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/reports',
    name: 'Reports',
    component: ReportsPage,
    meta: {
      title: 'Kasten • Reports List',
      docs: {
        path: '/operating/reporting',
        description: 'Reports',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/report/:name',
    name: 'ReportDetails',
    component: ReportDetailsPage,
    meta: {
      title: 'Kasten • Reports Details',
      docs: {
        path: '/operating/reporting',
        description: 'Reports',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/mc',
    name: 'MCSecondaryPage',
    component: MCSecondaryPage,
    beforeEnter: async (to, from) => {
      if (await shouldBlockMcSecondaryPages()) return false;
    },
    meta: {
      title: 'Kasten • Multi-Cluster',
      docs: {
        path: '/multicluster',
        description: 'Multi-Cluster Manager',
      },
      requiresAuth: true,
      layout: {
        component: DefaultLayout,
        wrapContentWithContainerClass: false,
      },
    },
  },
  {
    path: '/mc/join',
    name: 'MCJoin',
    component: MCJoin,
    beforeEnter: async (to, from) => {
      if (await shouldBlockMcSecondaryPages()) return false;
      if (await hasClusterInfo()) return { name: 'MCSecondaryPage' };
    },
    meta: {
      title: 'Kasten • Join a Multi-Cluster',
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/mc/promote',
    name: 'MCPromote',
    component: MCPromote,
    beforeEnter: async (to, from) => {
      if (await shouldBlockMcSecondaryPages()) return false;
      if (await hasClusterInfo()) return { name: 'MCSecondaryPage' };
    },
    meta: {
      title: 'Kasten • Setup a Multi-Cluster',
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/multicluster/clusters',
    name: 'MCClusters',
    component: ClustersPage,
    meta: {
      title: 'Kasten • Clusters',
      docs: {
        path: '/multicluster',
        description: 'Multi-Cluster Manager',
      },
      requiresAuth: true,
      layout: DefaultLayout,
      isMC: true,
    },
  },
  {
    path: '/multicluster',
    redirect: { name: 'MCClusters' },
    component: MultiClusterPage,
    meta: {
      title: 'Kasten • Multi-Cluster',
      requiresAuth: true,
      layout: {
        component: DefaultLayout,
        wrapContentWithContainerClass: false,
      },
      isMC: true,
    },
    children: [
      {
        path: 'profiles/location',
        name: 'MCLocationProfiles',
        component: LocationProfiles,
        meta: {
          title: 'Kasten • Global Location Profiles',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'profiles/location/:namespace/:name',
        name: 'MCLocationProfileView',
        component: LocationProfileView,
        meta: {
          title: 'Kasten • Global Location Profiles • View',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'profiles/location/create',
        name: 'MCLocationProfileCreate',
        component: LocationProfileForm,
        meta: {
          title: 'Kasten • Global Location Profiles • Create',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'profiles/location/edit/:namespace/:name',
        name: 'MCLocationProfileEdit',
        component: LocationProfileForm,
        meta: {
          title: 'Kasten • Global Location Profiles • Edit',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'profiles/infra',
        name: 'MCInfraProfiles',
        component: InfraProfiles,
        meta: {
          title: 'Kasten • Global Infrastructure Profiles',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'profiles/infra/create',
        name: 'MCInfraProfileCreate',
        component: InfraProfileForm,
        meta: {
          title: 'Kasten • Global Infra Profiles • Create',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'profiles/infra/edit/:namespace/:name',
        name: 'MCInfraProfileEdit',
        component: InfraProfileForm,
        meta: {
          title: 'Kasten • Global Infra Profiles • Edit',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'rbac',
        name: 'MCRBAC',
        component: RBAC,
        meta: {
          title: 'Kasten • Global RBAC Resources',
          docs: {
            path: '/multicluster/user_access',
            description: 'Multi-Cluster User Access',
          },
          requiresAuth: true,
        },
        props: { global: true },
      },
      {
        path: 'policies',
        meta: {
          requiresAuth: true,
        },
        props: { global: true },
        children: [
          {
            path: ':action?/:name?',
            name: 'MCPolicies',
            component: NewPoliciesPage,
            meta: {
              title: 'Kasten • Global Policies',
              docs: {
                path: '/multicluster/global_resources',
                description: 'Global Resources',
              },
              requiresAuth: true,
            },
            props: { global: true },
          },
          {
            path: 'view/:namespace/:name',
            name: 'MCPolicyView',
            component: PolicyViewPage,
            meta: {
              title: 'Kasten • View Global Policy',
              docs: {
                path: '/multicluster/global_resources',
                description: 'Global Resources',
              },
              requiresAuth: true,
            },
            props: { global: true },
          },
        ],
      },
      {
        path: 'distributions',
        name: 'MCDistributions',
        component: newDistributionsPageEnabled ? NewDistributionsPage : DistributionsPage,
        meta: {
          title: 'Kasten • Global Resource Distributions',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
      },
      ...(newDistributionsPageEnabled
        ? [
            {
              path: 'distributions/:namespace/:name',
              name: 'MCDistributionView',
              component: NewDistributionView,
              meta: {
                title: 'Kasten • Global Resource Distributions • View',
                docs: {
                  path: '/multicluster/global_resources',
                  description: 'Global Resources',
                },
                requiresAuth: true,
              },
            },
            {
              path: 'distributions/create',
              name: 'MCDistributionCreate',
              component: NewDistributionFormPage,
              meta: {
                title: 'Kasten • Global Resource Distributions • Create',
                docs: {
                  path: '/multicluster/global_resources',
                  description: 'Global Resources',
                },
                requiresAuth: true,
              },
            },
            {
              path: 'distributions/edit/:namespace/:name',
              name: 'MCDistributionEdit',
              component: NewDistributionFormPage,
              meta: {
                title: 'Kasten • Global Resource Distributions • Edit',
                docs: {
                  path: '/multicluster/global_resources',
                  description: 'Global Resources',
                },
                requiresAuth: true,
              },
            },
          ]
        : []),
      {
        path: 'licensing',
        name: 'MCLicensing',
        component: LicensingPage,
        meta: {
          title: 'Kasten • Global Licensing',
          docs: {
            path: '/multicluster/global_resources',
            description: 'Global Resources',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'tokens',
        name: 'MCJoinTokens',
        component: JoinTokensPage,
        meta: {
          title: 'Kasten • Join Tokens',
          docs: {
            path: '/multicluster/getting_started#join-tokens',
            description: 'Join Tokens',
          },
          requiresAuth: true,
        },
      },
    ],
  },
  {
    // Legacy VBR redirect links (e.g. /restore/tkc1-2-1?filter=appsandclusterscope).
    // Kept for backwards compatibility — VBR generates these and cannot change them.
    // Map the namespace param and legacy `filter` query onto the new RestorePointsCatalog
    // query so the namespace/policy filters pre-apply as before.
    path: '/restore/:namespace',
    name: 'Restore',
    redirect: (to) => buildLegacyRestoreRedirect(to.params.namespace as string, to.query.filter),
  },
  {
    path: '/restore-points',
    name: 'RestorePointsCatalog',
    component: RestorePointsCatalogPage,
    meta: {
      title: 'Kasten • Restore Points',
      docs: {
        path: '/usage/restore',
        description: 'Restoring Applications',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/restore-points/validate/:namespace/',
    name: 'ValidateRestorePoint',
    component: RestorePointValidateFormPage,
    meta: {
      title: 'Kasten • Restore Point Validate',
      // TODO: Add new page for the Validation in the docs.
      docs: {
        path: '/usage/restore',
        description: 'Restoring Applications',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  // Top-level pages formerly under Settings/
  {
    path: '/profiles',
    component: ProfilesPage,
    redirect: { name: 'LocationProfiles' },
    meta: {
      title: 'Kasten • Profiles',
      requiresAuth: true,
      layout: DefaultLayout,
    },
    children: [
      {
        path: 'infra',
        name: 'InfraProfiles',
        component: InfraProfiles,
        meta: {
          title: 'Kasten • Infra Profiles',
          requiresAuth: true,
        },
      },
      {
        path: 'infra/create',
        name: 'InfraProfileCreate',
        component: InfraProfileForm,
        meta: {
          title: 'Kasten • Infra Profiles • Create',
          requiresAuth: true,
        },
      },
      {
        path: 'infra/edit/:namespace/:name',
        name: 'InfraProfileEdit',
        component: InfraProfileForm,
        meta: {
          title: 'Kasten • Infrastructure Profiles • Edit',
          docs: {
            path: '/api/profiles',
            description: 'Infrastructure Profiles',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'profiles/callback',
        name: 'VeeamVaultCallback',
        component: VeeamVaultCallback,
        meta: {
          title: 'Kasten • Veeam Vault',
          requiresAuth: true,
          layout: DefaultLayout,
        },
      },
      {
        path: 'location',
        name: 'LocationProfiles',
        component: LocationProfiles,
        meta: {
          title: 'Kasten • Location Profiles',
          docs: {
            path: '/api/profiles',
            description: 'Location Profiles',
          },
          requiresAuth: true,
          layout: {
            component: DefaultLayout,
            wrapContentWithContainerClass: false,
          },
        },
      },
      {
        path: 'location/:namespace/:name',
        name: 'LocationProfileView',
        component: LocationProfileView,
        meta: {
          title: 'Kasten • Location Profiles • View',
          docs: {
            path: '/api/profiles',
            description: 'Location Profiles',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'location/create',
        name: 'LocationProfileCreate',
        component: LocationProfileForm,
        meta: {
          title: 'Kasten • Location Profiles • Create',
          docs: {
            path: '/api/profiles',
            description: 'Location Profiles',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'location/edit/:namespace/:name',
        name: 'LocationProfileEdit',
        component: LocationProfileForm,
        meta: {
          title: 'Kasten • Location Profiles • Edit',
          docs: {
            path: '/api/profiles',
            description: 'Location Profiles',
          },
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/transformsets',
    component: TransformSetSettings,
    redirect: { name: 'TransformSetsList' },
    meta: {
      requiresAuth: true,
      layout: DefaultLayout,
    },
    children: [
      {
        path: 'list',
        name: 'TransformSetsList',
        component: CustomTransformSetsSettings,
        meta: {
          title: 'Kasten • Transform Sets',
          docs: {
            path: '/usage/transformsets',
            description: 'Transform sets',
          },
        },
      },
      {
        path: 'examples',
        name: 'TransformSetsExamples',
        component: TransformSetsExamplesSettings,
        meta: {
          title: 'Kasten • Transform Sets • Examples',
          docs: {
            path: '/usage/transformsets',
            description: 'Transform set examples',
          },
        },
      },
      {
        path: 'edit/:namespace/:name',
        name: 'TransformSetEdit',
        component: TransformSetEditSettings,
        meta: {
          title: 'Kasten • Transform Sets Settings • Edit Transform Set',
          docs: {
            path: '/usage/transformsets',
            description: "Transform set's details",
          },
        },
      },
      {
        path: 'create',
        name: 'TransformSetCreate',
        component: TransformSetCreateSettings,
        meta: {
          title: 'Kasten • Transform Sets Settings • Create Transform Set',
          docs: {
            path: '/usage/transformsets',
            description: "Transform set's details",
          },
        },
      },
    ],
  },
  {
    path: '/blueprints',
    name: 'BlueprintsList',
    component: BlueprintsList,
    meta: {
      title: 'Kasten • Blueprints',
      docs: {
        path: '/kanister/testing',
        description: 'Blueprints',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/blueprints/bindings',
    name: 'BlueprintBindings',
    component: BlueprintBindings,
    meta: {
      title: 'Kasten • Blueprint Bindings',
      docs: {
        path: '/api/blueprintbindings',
        description: 'Blueprint Bindings',
      },
      requiresAuth: true,
      layout: {
        component: DefaultLayout,
        wrapContentWithContainerClass: false,
      },
    },
  },
  {
    path: '/blueprints/bindings',
    component: BlueprintBindingsPage,
    redirect: { name: 'BlueprintBindings' },
    meta: {
      requiresAuth: true,
      layout: DefaultLayout,
    },
    children: [
      {
        path: 'create',
        name: 'BlueprintBindingCreate',
        component: BlueprintBindingCreate,
        meta: {
          title: 'Kasten • Blueprint Bindings • Create Blueprint Binding',
          docs: {
            path: '/api/blueprintbindings',
            description: 'Blueprint Binding details',
          },
          requiresAuth: true,
        },
      },
      {
        path: ':namespace/:name/edit',
        name: 'BlueprintBindingEdit',
        component: BlueprintBindingEdit,
        meta: {
          title: 'Kasten • Blueprint Binding • Edit Blueprint Binding',
          docs: {
            path: '/api/blueprintbindings',
            description: 'Blueprint Binding form',
          },
          requiresAuth: true,
        },
      },
      {
        path: ':namespace/:name/view',
        name: 'BlueprintBindingView',
        component: BlueprintBindingView,
        meta: {
          title: 'Kasten • Blueprint Bindings • View Blueprint Binding',
          docs: {
            path: '/api/blueprintbindings',
            description: 'Blueprint Binding details',
          },
          requiresAuth: true,
        },
      },
    ],
  },

  {
    path: '/rbac',
    component: UserRolesPage,
    redirect: { name: 'UserRoles' },
    meta: {
      title: 'Kasten • RBAC Roles',
      requiresAuth: true,
      layout: DefaultLayout,
    },
    // TODO add Doc links
    // docs: { path: '/usage/configuration', description: 'Location Config' },
    children: [
      {
        path: 'roles',
        name: 'UserRoles',
        component: UserRoles,
        meta: {
          title: 'Kasten • RBAC Roles',
          requiresAuth: true,
        },
      },
      {
        path: 'assignments',
        name: 'UserRoleAssignments',
        component: UserRoleAssignments,
        meta: {
          title: 'Kasten • RBAC Bindings',
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/settings',
    component: SettingsPage,
    name: 'Settings',
    redirect: { name: 'SystemInfo' },
    meta: {
      title: 'Kasten • Settings',
      requiresAuth: true,
      layout: DefaultLayout,
    },
    children: [
      {
        path: 'info',
        name: 'SystemInfo',
        component: SystemInfo,
        meta: {
          title: 'Kasten • Support',
          docs: {
            path: '/operating/support',
            description: 'Support',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'license',
        name: 'LicenseSettings',
        component: LicenseSettings,
        meta: {
          title: 'Kasten • License Settings',
          docs: {
            path: '/install/advanced#starter-edition-and-licensing',
            description: 'Starter Edition and Licenses',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'drsetup',
        name: 'DRSetup',
        component: DrSetup,
        meta: {
          title: 'Kasten • Disaster Recovery Setup',
          docs: {
            path: '/operating/dr',
            description: 'Kasten Disaster Recovery Setup',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'drrestore',
        name: 'DRRestore',
        component: DrRestore,
        meta: {
          title: 'Kasten • Restore K10',
          docs: {
            path: '/operating/dr',
            description: 'Restore K10',
          },
          requiresAuth: true,
        },
      },
      {
        path: 'registrations',
        name: 'Registrations',
        component: RegistrationsPage,
        meta: {
          title: 'Kasten • Registrations',
          // TODO`add docs
          // docs: {
          //   path: '/operating/dr.html',
          //   description: 'Restore K10',
          // },
          requiresAuth: true,
        },
      },
      {
        path: 'passkey',
        name: 'PasskeyManagement',
        component: PasskeyManagementPage,
        meta: {
          title: 'Kasten • Passkey Management',
          // TODO`add docs
          // docs: {
          //   path: '/operating/dr.html',
          //   description: 'Restore K10',
          // },
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/support',
    name: 'Support',
    component: SupportPage,
    meta: {
      title: 'Kasten • Support',
      docs: {
        path: '/operating/support',
        description: 'Support',
      },
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/status',
    name: 'Status',
    component: StatusPage,
    meta: {
      title: 'Kasten • System Status',
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/userprofile',
    name: 'UserProfile',
    component: UserProfilePage,
    meta: {
      title: 'Kasten • User Profile',
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/vault',
    component: VeeamVaultCallback,
    meta: {
      title: 'Kasten • Vault',
      requiresAuth: true,
      layout: DefaultLayout,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound404Page,
    meta: { requiresAuth: true, layout: DefaultLayout },
  },
];

/**
 * Allows one redirect to avoid a redirect loop
 */
let redirected = false;
let progressTimeout: undefined | ReturnType<typeof setTimeout>;

const router = createRouter({
  /**
   * Vue maintains scroll position when changing pages, which is annoying.
   * https://router.vuejs.org/en/advanced/scroll-behavior.html
   * Equivilent to window.scrollTo(0, 0);
   */
  scrollBehavior(from, to, savedPosition) {
    // This is defined when the browser's back/forward buttons are pressed
    if (savedPosition) {
      return savedPosition;
    }

    // Handle anchor tags
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(
          () => {
            resolve({ el: to.hash, behavior: 'smooth' });
          },
          // Delay is needed to ensure the element is rendered before scrolling, I chose 50 ms, but we can adjust if needed or consider a more robust solution
          50,
        );
      });
    }

    // Don't scroll to top if only query params changed
    if (from.path === to.path) {
      return {};
    }

    // Scroll to top otherwise
    return { top: 0 };
  },
  history: createWebHashHistory(),
  routes,
});

router.beforeEach(async (to, from) => {
  const mainStore = useMainStore();
  const destinationRequiresAuth = to.meta?.requiresAuth === true;

  const userStore = useUserStore();
  if (destinationRequiresAuth && userStore.authUser === undefined) {
    await userStore.fetchAuthUser().catch((err) => {
      log.error('Error fetching auth user', err);
      return { name: 'Logout' };
    });
  }

  try {
    log.info(
      `Routing from ${String(from.name) || '—'} to ${String(to.name)}. Params`,
      to.params,
      'Query',
      to.query,
    );

    // Show the page load progress bar at top of the page
    // only if it takes more than 200ms
    clearTimeout(progressTimeout);
    progressTimeout = setTimeout(NProgress.start, 200);

    // Set the page title
    if (to.meta.title) document.title = to.meta.title as string;

    // Disable API requests on unauthenticated pages.
    mainStore.xhrEnabled = destinationRequiresAuth;

    /**
     * Fix for authentication workflow problem
     * When an unauthenticated user accesses a backend route, they will be redirected to the
     * frontend login page. This requires that the login page itself not be auth restricted.
     * To do this, the gateway must use rules that match the login path '/#/login' in the URL.
     * This fails because clients such as curl and chrome remove the hash fragments
     * (# and on) from the URL.  The workaround is to instead use a query param '/?page=Login' to
     * request the frontend login page and perform the re-route here.
     */
    const regex = /page=(\w+)/;
    const results = regex.exec(window.location.search); // eg '?page=Message/'
    const pageParam = results ? results[results.index] : null; // Get the regex matching group
    if (pageParam && !redirected) {
      log.info(`Found page param "${pageParam}" in URL. Re-routing to this page`);
      redirected = true;
      return { name: pageParam, query: to.query };
    }
  } catch (err) {
    log.warn('Error in router.js::beforeEach()', err);
  }
});

router.afterEach(() => {
  clearTimeout(progressTimeout);
  NProgress.done();
});

export default router;
